import { FC } from 'react';
import { css } from '@emotion/react';
import { noop } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { SsnField, FieldChangeEvent, Text } from '@frontend/design-system';
import { formatFieldLabel } from '../../../../../helpers';
import type { DynamicFormApi } from '../hooks/use-app-form';
import type { DynamicFormField } from '../types';

export interface SsnFieldProps {
  form: DynamicFormApi;
  fieldInfo: DynamicFormField;
}

const fieldLabelStyle = css`
  margin-bottom: ${theme.spacing(0.5)};
`;

export const SsnFormField: FC<SsnFieldProps> = ({ form, fieldInfo }) => {
  const { t } = useTranslation('digital-forms-experience');

  return (
    <div>
      <form.AppField
        name={fieldInfo.key}
        validators={{
          onChange: ({ value }) => {
            // Required validation
            if (fieldInfo.required && !value) {
              return t('This field is required');
            }

            // SSN format validation (must be 9 digits)
            if (value) {
              const ssnDigits = String(value).replace(/\D/g, '');
              if (ssnDigits.length > 0 && ssnDigits.length !== 9) {
                return t('SSN must be 9 digits');
              }
            }

            return undefined;
          },
        }}
      >
        {(field) => {
          const value = field.state.value as string;
          const hasError = field.state.meta.isTouched && field.state.meta.errors?.length > 0;
          const errorMessage = hasError ? field.state.meta.errors.join(', ') : '';

          const handleChange = (e?: FieldChangeEvent) => {
            if (!e) return;

            // SSN field uses custom event with { name, value } shape
            if ('value' in e && typeof e === 'object' && 'name' in e) {
              field.handleChange(e.value);
            }
          };

          return (
            <>
              <Text css={fieldLabelStyle}>{formatFieldLabel(fieldInfo.label, fieldInfo.required)}</Text>
              <SsnField
                id={field.name}
                name={field.name}
                label=''
                value={value ?? ''}
                error={errorMessage}
                touched={field.state.meta.isTouched}
                active={false}
                format='hyphenated'
                onBlur={field.handleBlur}
                onChange={handleChange}
                onFocus={noop}
                aria-invalid={hasError}
              />
            </>
          );
        }}
      </form.AppField>
    </div>
  );
};
